<template>
    <div
        class="voc-item-pill"
        data-cy="voc-item-pill"
        :class="{ 'voc-item-pill--blade': isBlade, 'voc-item-pill--cursor-pointer': canDownload }">
        <div
            class="voc-item-pill__details"
            data-cy="voc-item-pill-details"
            @mouseover="hoveringOnPill = true"
            @mouseleave="hoveringOnPill = false"
            @click="handleFileClick">
            <div class="voc-item-pill_imageContainer">
                <pendo-loading-indicator
                    v-if="loading"
                    data-cy="voc-feedback-form-pill-loading"
                    size="small" />
                <img
                    v-else-if="imagePreview && !isLink"
                    :src="imagePreview"
                    class="voc-item-pill__image"
                    :class="{ 'voc-item-pill__image--hover': hoveringOnPill && canDownload }"
                    data-cy="voc-feedback-form-pill-image">
                <img
                    v-else-if="linkImageUrl && isLink"
                    :src="linkImageUrl"
                    class="voc-item-pill__linkImage"
                    :class="{ 'voc-item-pill__linkImage--hover': hoveringOnPill }"
                    data-cy="voc-feedback-form-pill-image">
                <pendo-icon
                    v-show="hoveringOnPill && !loading && canDownload"
                    size="16"
                    :type="isLink ? 'external-link' : 'maximize-2'"
                    stroke="var(--palette-teal-70)"
                    class="voc-item-pill__icon"
                    data-cy="voc-feedback-form-pill-icon"
                    :class="{ 'voc-item-pill__icon--isLink': isLink }" />
            </div>
            <p
                v-if="!loading"
                v-pendo-tooltip="{
                    arrow: true,
                    content: `${isLink ? fileName : ''}`,
                    multiline: true,
                }"
                class="voc-item-pill__name"
                :class="{ 'voc-item-pill__name--hover': hoveringOnPill && canDownload }"
                data-cy="voc-feedback-form-pill-name">
                {{ isLink ? formatLinkName(fileName) : fileName }}
            </p>
        </div>
        <pendo-button
            v-if="isBlade && !isLink && !loading"
            v-pendo-tooltip="{
                arrow: true,
                content: 'Download image',
            }"
            theme="app"
            type="tertiary"
            class="voc-item-pill__action voc-item-pill__download"
            data-cy="voc-item-pill-download"
            icon="download"
            icon-size="16"
            @click="downloadAttachment" />
        <pendo-button
            v-if="canDelete && !loading"
            v-pendo-tooltip="{
                arrow: true,
                content: `${isBlade ? 'Delete' : 'Remove'} ${isLink ? 'link' : 'image'}`,
            }"
            theme="app"
            type="tertiary"
            class="voc-item-pill__action voc-item-pill__delete"
            data-cy="voc-item-pill-delete"
            icon="trash-2"
            icon-size="16"
            @click="deleteAttachment" />
    </div>
</template>

<script>
import { PendoIcon, PendoTooltip, PendoButton, PendoLoadingIndicator } from '@pendo/components';
import { mapState } from 'vuex';

export default {
    name: 'VocItemAttachmentPill',
    components: {
        PendoIcon,
        PendoButton,
        PendoLoadingIndicator,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        fileData: {
            type: Object,
            required: true,
        },
        blade: {
            type: Boolean,
            default: false,
        },
        canDelete: {
            type: Boolean,
            default: false,
        },
        canDownload: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['preview-image', 'delete'],
    data () {
        return {
            imagePreview: null,
            hoveringOnPill: false,
            linkImageUrl: null,
        };
    },
    computed: {
        ...mapState({
            localAttachmentList: (state) => state.vocItems.attachmentList,
        }),
        isLink () {
            return this.fileData.type === 'link';
        },
        isBlade () {
            return this.blade;
        },
        loading () {
            return !!this.fileData.loading;
        },
        fileName () {
            return this.fileData?.file?.name || 'Unknown';
        },
    },
    watch: {
        localAttachmentList () {
            this.setPillImage();
        },
        fileData () {
            this.setPillImage();
        },
    },
    created () {
        this.setPillImage();
    },
    methods: {
        handleFileClick () {
            if (this.fileData.type === 'image' && !this.fileData.loading) {
                this.$emit('preview-image');
            } else if (this.fileData.type === 'link' && !this.fileData.loading) {
                window.open(this.fileData.file.name);
            }
        },
        formatLinkName (link) {
            let updatedLink = link.replace(/^https?:\/\//i, '');
            updatedLink = updatedLink.replace(/^www\./i, '');

            return updatedLink;
        },
        setPillImage () {
            if (this.fileData?.type === 'image' && !this.fileData.loading) {
                const reader = new FileReader();
                reader.onload = () => {
                    this.imagePreview = reader.result;
                };
                reader.readAsDataURL(this.fileData.file);
            } else if (this.fileData?.type === 'link' && !this.fileData.loading) {
                const url = new URL(this.fileData.file.name);
                this.linkImageUrl = `https://www.google.com/s2/favicons?domain=${url.hostname}`;
            }
        },
        downloadAttachment () {
            const downloadLink = document.createElement('a');
            downloadLink.href = this.imagePreview;
            downloadLink.download = this.fileData.file.name;
            downloadLink.click();
            downloadLink.remove();
        },
        deleteAttachment () {
            this.$emit('delete', { id: this.fileData.id, type: this.fileData.type, trigger: 'pill' });
        },
    },
};
</script>

<!-- eslint-disable vue-scoped-css/no-parsing-error -->
<style lang="less" scoped>
.voc-item-pill {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid var(--palette-gray-40);
    border-radius: 3px;
    min-height: 37px !important;
    height: 37px;
    width: calc((100% - 11 * var(--space, 0px)) * 4 / 12 + (4 - 1) * var(--space, 0px));
    --space: 10px;
    overflow: hidden;

    &--blade {
        width: calc((100% - 11 * var(--space, 0px)) * 6 / 12 + (6 - 1) * var(--space, 0px));
    }

    &--cursor-pointer {
        cursor: pointer;
    }

    &__image {
        height: 100%;
        width: 37px;
        flex-grow: 0;
        flex-shrink: 0;
        object-fit: contain;
        padding-right: 1px;

        &--hover {
            opacity: 0.2;
        }
    }

    &__linkImage {
        &--hover {
            opacity: 0.2;
        }
    }

    &_imageContainer {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 37px;
        height: 100%;
        position: relative;
        border-right: 1px solid var(--palette-gray-40);
        flex-grow: 0;
        flex-shrink: 0;
    }

    &__name {
        margin: 0;
        font-weight: 600;
        color: var(--palette-teal-70);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex-grow: 1;
        flex-shrink: 1;
        padding: 1px 0;

        &--hover {
            text-decoration: underline 1.5px;
        }
    }

    &__action {
        margin: 0;
        flex-grow: 0;
        flex-shrink: 0;
        border-left: 1px solid var(--palette-gray-0);
        border-radius: 0px;
        min-width: 36px !important;

        &:hover {
            border-left: 1px solid var(--palette-gray-40);
            background-color: var(--palette-gray-10);
        }
    }

    &__download {
        border-right: 1px solid var(--palette-gray-0);

        &:hover {
            border-right: 1px solid var(--palette-gray-40);
        }
    }

    &__icon {
        position: absolute;
        left: 11px;
        top: 10px;
        pointer-events: none;
    }

    &__details {
        height: 100%;
        display: flex;
        gap: 10px;
        margin-right: 10px;
        justify-content: space-between;
        align-items: center;
        overflow: hidden;
        flex-grow: 1;
        flex-shrink: 1;
    }
}
</style>
